import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Dialog, DialogContent, Select, MenuItem, FormControl, InputLabel, Box, CircularProgress, Typography } from "@mui/material";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import WarningIcon from "@mui/icons-material/Warning";
import TransformIcon from "@mui/icons-material/Transform";
import AddIcon from "@mui/icons-material/Add";
import styles from "./styles.module.css";
import { buscarServicoPorTipo } from "../../../services/api-planejamento-tributario.service";
import { copiarVersaoPrevia, marcarResumoVersaoPrevia, buscarValoresResumo, liberarVersaoPrevia, iniciarProjeto, aprovarProjetoPisCofins, reenviarCreditosPisCofins } from "../../../services/api-piscofins.service";
import AvisoCreditosPendentes from "../AvisoCreditosPendentes";
import { useVersoesEmProcessamento } from "../../../hooks/useVersoesEmProcessamento";
import { versoesOperationsService } from "../../../services/versoesOperations.service";
import { 
  buscarMotorPorScript,
  verificarSeVersaoEProjeto,
  verificarSeVersaoEstaBloqueada,
  verificarSePreviaEstaBloqueada
} from "../../../utils/versoes.utils";
import SnackBar from "../../SnackBar";
import Loading from "../../Loading";
import VersaoCard from "./VersaoCard";
import {
  usePermissoes,
  CHAVES_PIS_COFINS_VIEW,
  CHAVES_PIS_COFINS_EDIT,
  CHAVES_PIS_COFINS_APPROVE,
} from "../../../config/Context/PermissoesContext";

const motoresMap = {
  "Sob Despesa": "CONCILIACAO_SPED",
  Exclusões: "EXCLUSOES",
  Monofásico: "MONOFASICO",
  "Resumo Geral": "RESUMO_GERAL",
};

const ModalVersoesPisCofins = ({
  open,
  onClose,
  idPrevia,
  tipoPrevia = "PIS-COFINS",
  contextHook,
  dadosEmpresa = null,
  isProjeto = false,
  onDataUpdated,
}) => {
  const { pode } = usePermissoes();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingCreate, setLoadingCreate] = useState(false);
  const [objSnackBar, setObjSnackBar] = useState({ type: "", msg: "" });
  const [versoes, setVersoes] = useState([]);
  const [previaInfo, setPreviaInfo] = useState(dadosEmpresa);
  const [mostrarAreaAdicionar, setMostrarAreaAdicionar] = useState(false);
  const [versaoSelecionadaAdicionar, setVersaoSelecionadaAdicionar] = useState("");
  const [motoresSelecionadosAdicionar, setMotoresSelecionadosAdicionar] = useState([]);
  const [tiposPrevia, setTiposPrevia] = useState([]);
  const [loadingTiposPrevia, setLoadingTiposPrevia] = useState(false);
  const [modoProjeto, setModoProjeto] = useState(false);
  const [valoresResumoProjeto, setValoresResumoProjeto] = useState(null);
  const [loadingResumoProjeto, setLoadingResumoProjeto] = useState(false);
  const [versaoSelecionadaProjeto, setVersaoSelecionadaProjeto] = useState(null);
  const [loadingConfirmarProjeto, setLoadingConfirmarProjeto] = useState(false);
  const [loadingLiberar, setLoadingLiberar] = useState(false); // Estado para loading do botão Liberar
  const [loadingAprovar, setLoadingAprovar] = useState(false); // Estado para loading do botão Aprovar Projeto
  const [creditosPendentes, setCreditosPendentes] = useState(null); // { idPrevia, versaoId, pendentes } após aprovar com falha de crédito
  // Ref: id da prévia para a qual já recebemos dadosPrevia da API (evita effect dadosEmpresa sobrescrever)
  const idPreviaSetFromApiRef = useRef(null);
  // Função para recarregar versões
  const recarregarVersoes = useCallback(async () => {
    setLoading(true);
    try {
      const data = await versoesOperationsService.buscarVersoesNormalizadas(tipoPrevia, idPrevia);
      const list = Array.isArray(data) ? data : (data?.versoes || []);
      const versoesOrdenadas = [...list].sort((a, b) =>
        (Number(b.idPreviaVersao) || 0) - (Number(a.idPreviaVersao) || 0)
      );
      setVersoes(versoesOrdenadas);
    } catch (error) {
      console.error('Erro ao recarregar versões:', error);
    } finally {
      setLoading(false);
    }
  }, [tipoPrevia, idPrevia]);

  // Hook para gerenciar versões em processamento
  const {
    versoesEmProcessamento,
    iniciarPollingVersao,
    pararTodosPollings,
    verificarEIniciarPollingVersoes,
  } = useVersoesEmProcessamento(recarregarVersoes);
  
  const {
    setVersaoSelecionada,
    setDadosVersao,
    setPreviaInfo: setPreviaInfoContexto,
    limparVersaoSelecionada,
    limparDadosVersao,
    limparPreviaInfo,
  } = contextHook();

  // Buscar versões quando modal abre
  useEffect(() => {
    const fetchVersoes = async () => {
      if (!open || !idPrevia) return;
      
      setLoading(true);
      try {
        const data = await versoesOperationsService.buscarVersoesNormalizadas(tipoPrevia, idPrevia);
        
        if (data.dadosPrevia) {
          const previaNormalizada = {
            ...data.dadosPrevia,
            // Garantir que o contexto saiba qual é o id da PRÉVIA
            idPreviaPisCofins: data.dadosPrevia.idPreviaPisCofins ?? idPrevia,
          };

          // Substituir (não fazer merge) para não manter dados de outra prévia/CNPJ
          setPreviaInfo(previaNormalizada);
          setPreviaInfoContexto(previaNormalizada);
          idPreviaSetFromApiRef.current = idPrevia;
        }

        // Ordenar versões da mais nova para a mais antiga
        const versoesOrdenadas = [...(data || [])].sort((a, b) => {
          return (Number(b.idPreviaVersao) || 0) - (Number(a.idPreviaVersao) || 0);
        });
        setVersoes(versoesOrdenadas);
      } catch (error) {
        console.error('Erro ao carregar versões:', error);
        setObjSnackBar({
          type: "error",
          msg: "Erro ao carregar versões da prévia.",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchVersoes();
  }, [open, idPrevia, tipoPrevia]);

  useEffect(() => {
    if (!open) {
      idPreviaSetFromApiRef.current = null;
      return;
    }
    setObjSnackBar({ type: "", msg: "" });
    // Só preencher previaInfo a partir da listagem quando a API ainda não retornou dadosPrevia para esta prévia
    if (dadosEmpresa && idPreviaSetFromApiRef.current !== idPrevia) {
      const previaEmpresa = {
        ...dadosEmpresa,
        // Caso venha da listagem, garantir que idPreviaPisCofins seja propagado
        idPreviaPisCofins:
          dadosEmpresa.idPreviaPisCofins ??
          dadosEmpresa.idPrevia ??
          idPrevia ??
          null,
      };

      setPreviaInfo(previaEmpresa);
      setPreviaInfoContexto(previaEmpresa);
    }
  }, [open, dadosEmpresa, idPrevia, setPreviaInfoContexto]);

  // Buscar tipos de prévia (motores) da API
  useEffect(() => {
    const fetchTiposPrevia = async () => {
      // Carregar apenas se a área de adicionar estiver visível e ainda não tivermos os tipos carregados
      if (!open || !tipoPrevia || !mostrarAreaAdicionar) return;
      
      // Evitar recarregar se já tiver dados
      if (tiposPrevia.length > 0) return;

      setLoadingTiposPrevia(true);
      try {
        // Buscar serviço e tipos de prévia por tipo (uma única chamada)
        const response = await buscarServicoPorTipo(tipoPrevia);
        const servicoEncontrado = response?.servico || null;
        const tiposArray = response?.tipos || [];
        
        // Mapear tipos de prévia para formato de motores
        const tiposFormatados = tiposArray.map((tipo) => {
          // Tentar usar script da API primeiro, depois mapear pela descrição
          const script = tipo.script || motoresMap[tipo.descricao] || null;
          return {
            id: tipo.idTipoPrevia,
            idTipoPrevia: tipo.idTipoPrevia,
            descricao: tipo.descricao,
            script: script,
          };
        });

        setTiposPrevia(tiposFormatados);
      } catch (error) {
        setObjSnackBar({
          type: "error",
          msg: "Erro ao carregar motores disponíveis.",
        });
        setTiposPrevia([]);
      } finally {
        setLoadingTiposPrevia(false);
      }
    };

    fetchTiposPrevia();
  }, [open, tipoPrevia, mostrarAreaAdicionar, tiposPrevia.length]);

  // Verificar versões em processamento quando lista muda
  useEffect(() => {
    if (open && versoes.length > 0) {
      verificarEIniciarPollingVersoes(versoes);
    }
  }, [open, versoes, verificarEIniciarPollingVersoes]);

  // Função para obter tipos de prévia usados em uma versão
  const obterTiposPreviaDaVersao = useCallback((versaoId) => {
    const versao = versoes.find(v => v.idPreviaVersao === versaoId);
    if (!versao) {
      return [];
    }
    
    // Tentar extrair idTipoPrevia diretamente dos motores na versão
    const idTipoPreviaDiretos = versao?.versao?.detalhes?.flatMap((detalhe) =>
      detalhe?.motores?.map((motor) => motor?.idTipoPrevia).filter(Boolean)
    ) || [];
    
    if (idTipoPreviaDiretos.length > 0) {
      // Se encontrou idTipoPrevia diretamente, usar eles (remover duplicatas)
      const idsUnicos = [...new Set(idTipoPreviaDiretos)];
      // Garantir que são números
      const idsNumericos = idsUnicos.map(id => Number(id));
      return idsNumericos;
    }
    
    // Fallback: mapear scripts para tipos de prévia
    if (!versao.scriptsDisponiveis) {
      return [];
    }
    
    if (tiposPrevia.length === 0) {
      return [];
    }
    
    const scriptsDaVersao = Array.from(versao.scriptsDisponiveis);
    
    const tiposPreviaUsados = tiposPrevia.filter(tipo => 
      tipo.script && scriptsDaVersao.includes(tipo.script)
    );
    
    const idsMapeados = tiposPreviaUsados.map(tipo => tipo.idTipoPrevia);
    // Garantir que são números
    const idsNumericos = idsMapeados.map(id => Number(id));
    
    return idsNumericos;
  }, [versoes, tiposPrevia]);

  // Selecionar automaticamente a última versão quando área de adicionar for aberta
  useEffect(() => {
    if (mostrarAreaAdicionar && versoes.length > 0 && !versaoSelecionadaAdicionar) {
      // Ordenar versões por idPreviaVersao (maior = mais recente) ou dtCadastro
      const versoesOrdenadas = [...versoes].sort((a, b) => {
        // Priorizar por idPreviaVersao (maior = mais recente)
        if (b.idPreviaVersao !== a.idPreviaVersao) {
          return b.idPreviaVersao - a.idPreviaVersao;
        }
        // Se idPreviaVersao for igual, usar dtCadastro
        if (a.dtCadastro && b.dtCadastro) {
          return new Date(b.dtCadastro) - new Date(a.dtCadastro);
        }
        return 0;
      });
      
      const ultimaVersao = versoesOrdenadas[0];
      if (ultimaVersao) {
        setVersaoSelecionadaAdicionar(ultimaVersao.idPreviaVersao);
      }
    }
  }, [mostrarAreaAdicionar, versoes, versaoSelecionadaAdicionar]);

  // Atualizar seleção de motores quando versão mudar
  useEffect(() => {
    if (versaoSelecionadaAdicionar && tiposPrevia.length > 0 && versoes.length > 0) {
      const motoresUsados = obterTiposPreviaDaVersao(versaoSelecionadaAdicionar);
      // Garantir que são números
      const motoresNumericos = motoresUsados.map(id => Number(id));
      setMotoresSelecionadosAdicionar(motoresNumericos);
    } else if (!versaoSelecionadaAdicionar) {
      // Limpar motores se não houver versão selecionada
      setMotoresSelecionadosAdicionar([]);
    }
  }, [versaoSelecionadaAdicionar, versoes, obterTiposPreviaDaVersao]);

  // Atualizar seleção de motores quando tiposPrevia carregar (useEffect separado para garantir timing)
  useEffect(() => {
    // Se tiposPrevia acabou de carregar e já temos versão selecionada, atualizar motores
    if (versaoSelecionadaAdicionar && tiposPrevia.length > 0 && versoes.length > 0) {
      const motoresUsados = obterTiposPreviaDaVersao(versaoSelecionadaAdicionar);
      // Garantir que são números
      const motoresNumericos = motoresUsados.map(id => Number(id));
      setMotoresSelecionadosAdicionar(motoresNumericos);
    }
  }, [tiposPrevia.length, versaoSelecionadaAdicionar, versoes, obterTiposPreviaDaVersao]);

  // Função para formatar valores monetários
  const formatCurrency = (valor = 0) =>
    new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(valor);

  const carregarValoresResumoVersao = useCallback(async (versaoId) => {
    setLoadingResumoProjeto(true);
    try {
      const valoresResumo = await buscarValoresResumo(versaoId);
      
      if (valoresResumo?.sucesso && valoresResumo?.valores) {
        // Processar valores por script
        const valoresPorScript = {};
        let totalPis = 0;
        let totalCofins = 0;
        let totalGeral = 0;

        Object.keys(valoresResumo.valores).forEach(script => {
          const valores = valoresResumo.valores[script];
          valoresPorScript[script] = {
            pis: valores.pis || 0,
            cofins: valores.cofins || 0,
            total: valores.total || 0,
          };
          
          totalPis += valores.pis || 0;
          totalCofins += valores.cofins || 0;
          totalGeral += valores.total || 0;
        });

        setValoresResumoProjeto({
          porScript: valoresPorScript,
          totais: {
            pis: totalPis,
            cofins: totalCofins,
            total: totalGeral,
          }
        });
      } else {
        setValoresResumoProjeto({
          porScript: {},
          totais: {
            pis: 0,
            cofins: 0,
            total: 0,
          }
        });
      }
    } catch (error) {
      console.error("Erro ao carregar valores do resumo:", error);
      setObjSnackBar({
        type: "error",
        msg: "Erro ao carregar valores do resumo para projeto.",
      });
      setValoresResumoProjeto(null);
    } finally {
      setLoadingResumoProjeto(false);
    }
  }, []);

  const handleAbrirProjeto = useCallback(async () => {
    // Garantir que área de adicionar está fechada
    setMostrarAreaAdicionar(false);
    setVersaoSelecionadaAdicionar("");
    setMotoresSelecionadosAdicionar([]);
    
    // Selecionar versão mais recente ABSOLUTA (sem filtrar por resumo primeiro)
    const versoesOrdenadas = [...versoes].sort((a, b) => {
      if (b.idPreviaVersao !== a.idPreviaVersao) {
        return b.idPreviaVersao - a.idPreviaVersao;
      }
      if (a.dtCadastro && b.dtCadastro) {
        return new Date(b.dtCadastro) - new Date(a.dtCadastro);
      }
      return 0;
    });

    const versaoMaisRecente = versoesOrdenadas[0];

    // Verificar se a versão mais recente tem resumo geral liberado
    const resumoScript = motoresMap["Resumo Geral"];
    const temResumo = versaoMaisRecente?.scriptsDisponiveis?.has(resumoScript) || versaoMaisRecente?.versao?.resumo === true;

    if (!temResumo) {
      setObjSnackBar({
        type: "error",
        msg: "Não é possível iniciar esse projeto pois a última versão não possui um resumo geral disponível.",
      });
      return;
    }

    setVersaoSelecionadaProjeto(versaoMaisRecente.idPreviaVersao);
    setModoProjeto(true);
    
    // Carregar valores da versão selecionada
    await carregarValoresResumoVersao(versaoMaisRecente.idPreviaVersao);
  }, [versoes, carregarValoresResumoVersao, loadingResumoProjeto, versoesEmProcessamento]); // Adicionei dependências

  // Efeito para abrir modo projeto automaticamente se solicitado
  useEffect(() => {
    if (open && isProjeto && versoes.length > 0 && !modoProjeto && !versaoSelecionadaProjeto) {
      // Pequeno delay para garantir que renderização aconteceu
      const timer = setTimeout(() => {
        handleAbrirProjeto();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open, isProjeto, versoes, modoProjeto, versaoSelecionadaProjeto, handleAbrirProjeto]);



  const handleMudarVersaoProjeto = async (versaoId) => {
    setVersaoSelecionadaProjeto(versaoId);
    await carregarValoresResumoVersao(versaoId);
  };

  const handleConfirmarProjeto = async () => {
    if (!versaoSelecionadaProjeto) {
      setObjSnackBar({
        type: "error",
        msg: "Por favor, selecione uma versão para converter em projeto.",
      });
      return;
    }

    setLoadingConfirmarProjeto(true);
    try {
      const response = await iniciarProjeto(idPrevia, versaoSelecionadaProjeto);

      if (response?.sucesso) {
        setObjSnackBar({
          type: "success",
          msg: response.mensagem || "Projeto iniciado com sucesso! A prévia foi travada para análise.",
        });
        handleClose();
        setTimeout(() => {
          onDataUpdated?.();
        }, 300);
      } else {
        setObjSnackBar({
          type: "error",
          msg: response?.mensagem || "Erro ao iniciar projeto.",
        });
      }
    } catch (error) {
      console.error("Erro ao confirmar projeto:", error);
      setObjSnackBar({
        type: "error",
        msg: error?.response?.data?.mensagem || error?.message || "Erro ao iniciar projeto. Tente novamente.",
      });
    } finally {
      setLoadingConfirmarProjeto(false);
    }
  };

  const handleClose = () => {
    pararTodosPollings();
    setCreditosPendentes(null);
    setVersoes([]);
    setObjSnackBar({ type: "", msg: "" });
    setMostrarAreaAdicionar(false);
    setVersaoSelecionadaAdicionar("");
    setMotoresSelecionadosAdicionar([]);
    setModoProjeto(false);
    setValoresResumoProjeto(null);
    setVersaoSelecionadaProjeto(null);
    // Limpar contexto para não reutilizar dados de outra prévia ao reabrir
    limparVersaoSelecionada?.();
    limparDadosVersao?.();
    limparPreviaInfo?.();
    onClose?.();
  };

  const handleCancelarAdicionar = () => {
    setMostrarAreaAdicionar(false);
    setVersaoSelecionadaAdicionar("");
    setMotoresSelecionadosAdicionar([]);
  };

  const handleLiberarPrevia = async () => {
    setLoadingLiberar(true);
    try {
      const response = await liberarVersaoPrevia(idPrevia); 

      if (response?.sucesso || response?.message) {
        setObjSnackBar({
          type: "success",
          msg: response?.message || "Prévia liberada para edições com sucesso!",
        });
        handleClose();
        setTimeout(() => {
          onDataUpdated?.();
        }, 300);
      } else {
        setObjSnackBar({
          type: "error",
          msg: response?.mensagem || "Erro ao liberar prévia.",
        });
        setLoadingLiberar(false);
      }
    } catch (error) {
       console.error("Erro ao liberar prévia:", error);
       setObjSnackBar({
        type: "error",
        msg: error.response?.data?.message || error.message || "Erro ao tentar liberar prévia. Verifique se a funcionalidade está disponível.",
      });
      setLoadingLiberar(false);
    }
  };

  const handleConfirmarAprovacao = async () => {
    // Obter versaoId: suporta número (ID), objeto com idPreviaVersao, ou última versão como fallback
    let versaoId =
      typeof versaoSelecionadaProjeto === "number"
        ? versaoSelecionadaProjeto
        : versaoSelecionadaProjeto?.idPreviaVersao;

    if (!versaoId && versoes?.length > 0) {
      const versoesOrdenadas = [...versoes].sort((a, b) => {
        if (b.idPreviaVersao !== a.idPreviaVersao) {
          return b.idPreviaVersao - a.idPreviaVersao;
        }
        if (a.dtCadastro && b.dtCadastro) {
          return new Date(b.dtCadastro) - new Date(a.dtCadastro);
        }
        return 0;
      });
      versaoId = versoesOrdenadas[0]?.idPreviaVersao;
    }

    if (!versaoId || !idPrevia) {
      setObjSnackBar({
        type: "error",
        msg: "Não há versão disponível para aprovar a prévia.",
      });
      return;
    }

    setLoadingAprovar(true);
    try {
      const response = await aprovarProjetoPisCofins(idPrevia, {
        versaoId,
      });
      if (response?.sucesso || response?.message) {
        // O crédito é lançado só pelo backend (idempotente); aqui apenas refletimos o resultado.
        const pendentes = Number(response?.creditos?.pendentes) || 0;
        if (pendentes > 0) {
          setCreditosPendentes({ idPrevia, versaoId, pendentes });
          setObjSnackBar({
            type: "warning",
            msg: "Projeto aprovado, mas alguns créditos não foram lançados na Conta Corrente.",
          });
        } else {
          setObjSnackBar({
            type: "success",
            msg: "Projeto aprovado e créditos aplicados em Conta Corrente.",
          });
          handleClose();
          setTimeout(() => {
            onDataUpdated?.();
          }, 300);
        }
      } else {
        setObjSnackBar({
          type: "error",
          msg: response?.mensagem || "Erro ao aprovar projeto.",
        });
      }
    } catch (error) {
      console.error("Erro ao aprovar projeto:", error);
      setObjSnackBar({
        type: "error",
        msg: error.response?.data?.mensagem || error.response?.data?.message || error.message || "Erro ao aprovar projeto.",
      });
    } finally {
      setLoadingAprovar(false);
    }
  };

  const handleCopiarVersao = async () => {
    if (!pode(CHAVES_PIS_COFINS_EDIT)) {
      setObjSnackBar({
        type: "error",
        msg: "Você não tem permissão para criar ou copiar versões.",
      });
      return;
    }
    // Verificar se prévia está bloqueada
    if (previaEstaBloqueada) {
      setObjSnackBar({
        type: "error",
        msg: "Não é possível copiar versões enquanto a prévia estiver em análise ou aprovada.",
      });
      return;
    }

    // Validações
    if (!versaoSelecionadaAdicionar) {
      setObjSnackBar({
        type: "error",
        msg: "Por favor, selecione uma versão para copiar.",
      });
      return;
    }

    if (!motoresSelecionadosAdicionar || motoresSelecionadosAdicionar.length === 0) {
      setObjSnackBar({
        type: "error",
        msg: "Por favor, selecione pelo menos um motor.",
      });
      return;
    }

    setLoadingCreate(true);
    try {
      const response = await copiarVersaoPrevia(idPrevia, {
        versaoIdOriginal: parseInt(versaoSelecionadaAdicionar),
        tiposPreviaIds: motoresSelecionadosAdicionar.map(id => parseInt(id)),
      });

      if (response?.sucesso && response?.dados) {
        setObjSnackBar({
          type: "success",
          msg: response.mensagem || "Versão copiada com sucesso!",
        });
        setLoadingCreate(false);
        handleClose();
        setTimeout(() => {
          onDataUpdated?.();
        }, 300);
      } else {
        setObjSnackBar({
          type: "error",
          msg: response?.mensagem || "Erro ao copiar a versão",
        });
        setLoadingCreate(false);
      }
    } catch (error) {
      setObjSnackBar({
        type: "error",
        msg: error.response?.data?.mensagem || "Ops! Erro ao copiar versão.",
      });
      setLoadingCreate(false);
    }
  };

  // Verificar se há pelo menos uma versão com resumo disponível (base legal salva)
  const temVersaoComResumo = versoes.some((versao) => {
    const resumoScript = motoresMap["Resumo Geral"];
    return versao.scriptsDisponiveis?.has(resumoScript) || versao?.versao?.resumo === true;
  });

  // Verificar se a última versão (mais recente) tem resumo geral habilitado
  const ultimaVersaoTemResumo = (() => {
    if (versoes.length === 0) return false;
    
    // Ordenar versões da mais nova para a mais antiga
    const versoesOrdenadas = [...versoes].sort((a, b) => {
      if (b.idPreviaVersao !== a.idPreviaVersao) {
        return b.idPreviaVersao - a.idPreviaVersao;
      }
      if (a.dtCadastro && b.dtCadastro) {
        return new Date(b.dtCadastro) - new Date(a.dtCadastro);
      }
      return 0;
    });
    
    const ultimaVersao = versoesOrdenadas[0];
    if (!ultimaVersao) return false;
    
    const resumoScript = motoresMap["Resumo Geral"];
    return ultimaVersao.scriptsDisponiveis?.has(resumoScript) || ultimaVersao?.versao?.resumo === true;
  })();

  // Verificar se prévia está bloqueada (qualquer versão EM_ANALISE ou APROVADO, ou status da prévia)
  const previaEstaBloqueada = verificarSePreviaEstaBloqueada(versoes, previaInfo);
  
  // Verificar se o status da prévia é PROJETO-INICIADO
  const statusPrevia = previaInfo?.status || dadosEmpresa?.status;
  const eProjetoIniciado = statusPrevia === 'PROJETO-INICIADO';
  
  // Verificar se pode criar versão (não bloqueada E não é projeto iniciado)
  const podeCriarVersao = !previaEstaBloqueada && !eProjetoIniciado;
  
  // Verificar se pode copiar versão (não bloqueada E não é projeto iniciado)
  const podeCopiarVersao = !previaEstaBloqueada && !eProjetoIniciado;

  const handleAnalisar = async (versao, script) => {
    if (!pode(CHAVES_PIS_COFINS_VIEW)) {
      setObjSnackBar({
        type: "error",
        msg: "Você não tem permissão para acessar os motores desta prévia.",
      });
      return;
    }
    if (!versao?.idPreviaVersao || !idPrevia) {
      return;
    }

    // Se for Resumo Geral, marcar resumo no MongoDB antes de navegar
    if (script === "RESUMO_GERAL") {
      try {
        await marcarResumoVersaoPrevia(versao.idPreviaVersao);
      } catch (error) {
        // Continuar navegação mesmo se houver erro ao marcar resumo
      }
    }

    const motorInfo = buscarMotorPorScript(versao, script);

    // Mapear script para rota
    const rotasPorScript = {
      CONCILIACAO_SPED: "sob-despesas",
      EXCLUSOES: "exclusoes",
      MONOFASICO: "monofasico",
      RESUMO_GERAL: "resumo-geral",
    };

    const rota = rotasPorScript[script];

    if (!rota) {
      setObjSnackBar({
        type: "error",
        msg: "Rota não configurada para este motor.",
      });
      return;
    }

    // Salvar dados no contexto
    setVersaoSelecionada({
      ...versao,
      motorSelecionado: motorInfo
        ? { ...motorInfo, script }
        : { script, nome: script },
    });

    const matriz = versao?.versao?.matriz || {};
    const empresa = versao?.versao?.empresa || {};
    const periodoListagem =
      dadosEmpresa?.dtInicial && dadosEmpresa?.dtFinal
        ? {
            dtInicial: dadosEmpresa.dtInicial,
            dtFinal: dadosEmpresa.dtFinal,
          }
        : null;

    const dadosVersaoPayload = {
      versaoId: versao.idPreviaVersao,
      script,
      motorId: motorInfo?.id ?? null,
      documentId: motorInfo?.documentId ?? null,
      status: motorInfo?.status ?? null,
      cnpjMatriz: matriz?.cnpj || null,
      nomeEmpresa:
        empresa?.razaoSocial ||
        matriz?.razaoSocial ||
        versao?.versao?.razaoSocial ||
        null,
      periodo:
        versao?.periodoReferencia ||
        versao?.versao?.periodo ||
        versao?.versao?.competencia ||
        versao?.versao?.mesReferencia ||
        periodoListagem ||
        null,
      // Vincular explicitamente a prévia de origem
      idPreviaPisCofins:
        versao?.versao?.idPreviaPisCofins ??
        versao?.versao?.idPrevia ??
        idPrevia ??
        null,
    };

    setDadosVersao(dadosVersaoPayload);

    // Navegar para a página específica do motor
    navigate(`/piscofins/previas/${versao.idPreviaVersao}/${rota}`);
    
    // Fechar modal
    onClose?.();
  };

  if (!open) return null;

  // Depois de aprovar com crédito pendente o modal fica aberto; qualquer forma de fechar
  // (botão do aviso ou clique fora) precisa atualizar a lista, como a aprovação sem pendência faz.
  const fecharAposAprovacaoComPendencia = () => {
    handleClose();
    setTimeout(() => {
      onDataUpdated?.();
    }, 300);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={creditosPendentes ? fecharAposAprovacaoComPendencia : handleClose}
        maxWidth="sm"
        fullWidth
      >
        {loading ? (
          <Loading loading={loading} />
        ) : (
          <DialogContent>
            {creditosPendentes && (
              <AvisoCreditosPendentes
                pendentes={creditosPendentes.pendentes}
                onReenviar={async () =>
                  (
                    await reenviarCreditosPisCofins(creditosPendentes.idPrevia, {
                      versaoId: creditosPendentes.versaoId,
                    })
                  )?.creditos
                }
                onFechar={fecharAposAprovacaoComPendencia}
              />
            )}
            <div className={styles.containerTitle}>
              <div className={styles.title}>
                <FormatListNumberedIcon /> Versões da Prévia
              </div>

              <div className={styles.containerButtons}>
                {pode(CHAVES_PIS_COFINS_EDIT) &&
                  ultimaVersaoTemResumo &&
                  statusPrevia !== "PROJETO-APROVADO" &&
                  statusPrevia !== "PREVIA-CANCELADA" && (
                  <Button
                    variant="outlined"
                    onClick={handleAbrirProjeto}
                    className={styles.btnProjeto}
                    size="small"
                    disabled={Object.keys(versoesEmProcessamento).length > 0 || loadingResumoProjeto || previaEstaBloqueada && !eProjetoIniciado}
                    startIcon={<TransformIcon />}
                  >
                    {loadingResumoProjeto ? "Carregando..." : "Projeto"}
                  </Button>
                )}

                {pode(CHAVES_PIS_COFINS_EDIT) && (
                <Button
                  variant="contained"
                  onClick={() => {
                    setModoProjeto(false);
                    setMostrarAreaAdicionar(true);
                  }}
                  className={styles.btnAnalisar}
                  size="small"
                  disabled={Object.keys(versoesEmProcessamento).length > 0 || !podeCriarVersao}
                  startIcon={<AddIcon />}
                >
                  Versão
                </Button>
                )}
              </div>
            </div>
            
            {modoProjeto ? (
              // Vista de Projeto
              <>
                {/* Alerta de Atenção */}
                <div className={styles.alertaProjeto}>
                  <div className={styles.alertaTraco}></div>
                  <div className={styles.alertaConteudo}>
                    <WarningIcon className={styles.alertaIcone} />
                    <span className={styles.alertaTexto}>
                      Atenção: Após a conversão, a prévia será travada e não será possível criar ou editar versões até que o cliente aprove ou rejeite o projeto.
                    </span>
                  </div>
                </div>

                {/* Card Único com Seletor e Resumo */}
                <div className={styles.containerProjeto}>
                  <div className={styles.cardProjeto}>
                    {/* Seletor de Versão - REMOVIDO a pedido */}
                    {/* A versão mais recente já é selecionada automaticamente no handleAbrirProjeto */}

                    {/* Título do Resumo */}
                    <h3 className={styles.tituloProjeto}>Resumo do Crédito a Aprovar</h3>

                    {/* Conteúdo do Resumo */}
                    {loadingResumoProjeto ? (
                      <Loading loading={true} />
                    ) : valoresResumoProjeto ? (
                      <>
                        <div className={styles.listaCreditos}>
                          {tiposPrevia.map((tipo) => {
                            const valores = valoresResumoProjeto?.porScript[tipo.script] || { pis: 0, cofins: 0, total: 0 };
                            const itens = [];
                            
                            // Mostrar PIS apenas se tiver crédito
                            if (valores.pis > 0) {
                              itens.push(
                                <div key={`${tipo.idTipoPrevia}-pis`} className={styles.itemCredito}>
                                  <span className={styles.labelCredito}>PIS - {tipo.descricao}</span>
                                  <span className={styles.valorCredito}>{formatCurrency(valores.pis)}</span>
                                </div>
                              );
                            }
                            
                            // Mostrar COFINS apenas se tiver crédito
                            if (valores.cofins > 0) {
                              itens.push(
                                <div key={`${tipo.idTipoPrevia}-cofins`} className={styles.itemCredito}>
                                  <span className={styles.labelCredito}>COFINS - {tipo.descricao}</span>
                                  <span className={styles.valorCredito}>{formatCurrency(valores.cofins)}</span>
                                </div>
                              );
                            }
                            
                            return itens;
                          }).flat()}
                          
                          {/* Total de Crédito */}
                          <div className={styles.itemCreditoTotal}>
                            <span className={styles.labelCreditoTotal}>Total de Crédito</span>
                            <span className={styles.valorCreditoTotal}>{formatCurrency(valoresResumoProjeto?.totais.total || 0)}</span>
                          </div>
                          <div className={styles.itemCredito}>
                            <span className={styles.labelCredito}>PIS</span>
                            <span className={styles.valorCredito}>{formatCurrency(valoresResumoProjeto?.totais.pis || 0)}</span>
                          </div>
                          <div className={styles.itemCredito}>
                            <span className={styles.labelCredito}>COFINS</span>
                            <span className={styles.valorCredito}>{formatCurrency(valoresResumoProjeto?.totais.cofins || 0)}</span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div>Nenhum dado disponível</div>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Área de Adicionar Versão */}
                {mostrarAreaAdicionar && (
              <div className={styles.containerAdicionar}>
                <div className={styles.areaAdicionar}>
                  {!loadingCreate && (
                  <>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2 }}>
                    {/* Seleção de Versão */}
                    <FormControl fullWidth size="small">
                      <InputLabel id="select-versao-label" size="small">Selecionar Versão</InputLabel>
                      <Select
                        labelId="select-versao-label"
                        id="select-versao"
                        value={versaoSelecionadaAdicionar}
                        label="Selecionar Versão"
                        size="small"
                        onChange={(e) => setVersaoSelecionadaAdicionar(e.target.value)}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "#18316e",
                            },
                            "&:hover fieldset": {
                              borderColor: "#204091",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#18316e",
                            },
                          },
                        }}
                      >
                        {versoes.map((versao) => (
                          <MenuItem key={versao.idPreviaVersao} value={versao.idPreviaVersao}>
                            {versao.descricao || `Versão ${versao.idPreviaVersao}`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Seleção de Motores (Tipos de Prévia) */}
                    <FormControl fullWidth size="small">
                      <InputLabel id="select-motores-label" size="small">Selecionar Motores</InputLabel>
                      <Select
                        labelId="select-motores-label"
                        id="select-motores"
                        multiple
                        value={motoresSelecionadosAdicionar || []}
                        label="Selecionar Motores"
                        size="small"
                        disabled={loadingTiposPrevia || tiposPrevia.length === 0}
                        onChange={(e) => {
                          // Garantir que os valores sejam números
                          const valores = Array.isArray(e.target.value) 
                            ? e.target.value.map(id => Number(id))
                            : [];
                          setMotoresSelecionadosAdicionar(valores);
                        }}
                        renderValue={(selected) => {
                          // Garantir que a comparação funcione com números
                          const selectedNumericos = selected.map(id => Number(id));
                          const selecionados = tiposPrevia.filter((tipo) =>
                            selectedNumericos.includes(Number(tipo.idTipoPrevia))
                          );
                          return selecionados.map((tipo) => tipo.descricao).join(", ");
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "#18316e",
                            },
                            "&:hover fieldset": {
                              borderColor: "#204091",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#18316e",
                            },
                          },
                        }}
                      >
                        {tiposPrevia.map((tipo) => (
                          <MenuItem key={tipo.idTipoPrevia} value={Number(tipo.idTipoPrevia)}>
                            {tipo.descricao}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Botões Cancelar e Salvar */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={handleCancelarAdicionar}
                      className={styles.btnFechar}
                      size="small"
                    >
                      Cancelar
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleCopiarVersao}
                      className={styles.btnAnalisar}
                      size="small"
                      disabled={!pode(CHAVES_PIS_COFINS_EDIT) || loadingCreate || !versaoSelecionadaAdicionar || !motoresSelecionadosAdicionar || motoresSelecionadosAdicionar.length === 0 || !podeCopiarVersao}
                    >
                      {loadingCreate ? "Copiando..." : "Salvar"}
                    </Button>
                  </Box>
                  </>
                  )}
                </div>
              </div>
            )}
            
                <div className={styles.containerVersao}>
                  {(Array.isArray(versoes) ? versoes : []).map((versao) => {
                    const isProjeto = verificarSeVersaoEProjeto(versao);
                    const isBloqueada = verificarSeVersaoEstaBloqueada(versao);
                    // Se prévia está bloqueada, todas as versões seguem o mesmo comportamento
                    const versaoBloqueada = previaEstaBloqueada ? true : isBloqueada;
                    
                    return (
                      <VersaoCard
                        key={versao.idPreviaVersao}
                        versao={versao}
                        motoresMap={motoresMap}
                        handleAnalisar={handleAnalisar}
                        status={versoesEmProcessamento[versao.idPreviaVersao]?.status}
                        dadosProgresso={versoesEmProcessamento[versao.idPreviaVersao]?.dadosProgresso}
                        emProcessamento={!!versoesEmProcessamento[versao.idPreviaVersao]}
                        isProjeto={isProjeto}
                        isBloqueada={versaoBloqueada}
                        isProjetoIniciado={eProjetoIniciado}
                        podeNavegarMotores={pode(CHAVES_PIS_COFINS_VIEW)}
                      />
                    );
                  })}
                </div>
              </>
            )}
            
            {/* Botões Fechar e Confirmar - sempre visíveis SE NÃO ESTIVER EM MODO PROJETO */}
            {!modoProjeto && (
            <div className={styles.containerBtn}>
              <Button
                variant="outlined"
                onClick={handleClose}
                className={styles.btnFechar}
              >
                Fechar
              </Button>
              
               {/* Novos Botões de Rodapé */}
               <div style={{ flex: 1 }}></div> {/* Espaçador */}
               
               {previaEstaBloqueada && !modoProjeto && !eProjetoIniciado && statusPrevia !== 'PROJETO-APROVADO' && statusPrevia !== 'PREVIA-CANCELADA' && (
                 <>
                   {pode(CHAVES_PIS_COFINS_EDIT) && (
                   <Button
                      variant="text"
                      color="secondary"
                      onClick={handleLiberarPrevia}
                      disabled={loadingLiberar}
                      size="small"
                      sx={{ marginRight: 1 }}
                   >
                     {loadingLiberar ? "Liberando..." : "Liberar Prévia"}
                   </Button>
                   )}
                   
                   {pode(CHAVES_PIS_COFINS_APPROVE) && (
                   <Button
                      variant="contained"
                      color="primary"
                      onClick={handleConfirmarAprovacao}
                      disabled={loadingAprovar || Boolean(creditosPendentes)}
                      size="small"
                   >
                     {loadingAprovar ? "Aprovando..." : "Confirmar Aprovação"}
                   </Button>
                   )}
                 </>
               )}
            </div>
            )}

            {modoProjeto && (
                <div className={styles.containerBtn}>
                  {eProjetoIniciado ? (
                     // Layout para PROJETO-INICIADO: Cancelar (Esq) ... Liberar | Aprovar (Dir)
                     <>
                        <Button
                            variant="outlined"
                            onClick={() => {
                              setModoProjeto(false);
                              setVersaoSelecionadaProjeto(null);
                              setValoresResumoProjeto(null);
                            }}
                            className={styles.btnFechar}
                          >
                            Cancelar
                        </Button>
                        <div style={{ flex: 1 }}></div>
                        {pode(CHAVES_PIS_COFINS_EDIT) && (
                        <Button
                            variant="outlined"
                            onClick={handleLiberarPrevia}
                            disabled={loadingLiberar}
                            className={styles.btnFechar}
                        >
                            {loadingLiberar ? "Liberando..." : "Liberar Prévia"}
                        </Button>
                        )}
                        {pode(CHAVES_PIS_COFINS_APPROVE) && (
                        <Button
                            variant="contained"
                            onClick={handleConfirmarAprovacao}
                            disabled={loadingAprovar || Boolean(creditosPendentes)}
                            className={styles.btnAnalisar}
                        >
                            {loadingAprovar ? "Aprovando..." : "Aprovar Projeto"}
                        </Button>
                        )}
                     </>
                  ) : (
                    // Layout Padrão de Projeto: Cancelar ... Confirmar
                     <div className={styles.containerBtnProjeto} style={{ width: '100%', justifyContent: 'space-between' }}>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setModoProjeto(false);
                          setVersaoSelecionadaProjeto(null);
                          setValoresResumoProjeto(null);
                        }}
                        className={styles.btnFechar}
                      >
                        Cancelar
                      </Button>
                      {pode(CHAVES_PIS_COFINS_EDIT) && (
                      <Button
                        variant="contained"
                        onClick={handleConfirmarProjeto}
                        className={styles.btnAnalisar}
                        disabled={loadingConfirmarProjeto || !versaoSelecionadaProjeto}
                      >
                        {loadingConfirmarProjeto ? "Confirmando..." : "Confirmar Projeto"}
                      </Button>
                      )}
                    </div>
                  )}
                </div>
            )}
          </DialogContent>
        )}
      </Dialog>

      <SnackBar objSnackBar={objSnackBar} setObjSnackBar={setObjSnackBar} />
    </>
  );
};

export default ModalVersoesPisCofins;

